import axios from 'axios'
import React from 'react'
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'
import {
  MdAdd,
  MdCheck,
  MdChecklist,
  MdClose,
  MdDeleteOutline,
  MdEvent,
  MdGroupAdd,
  MdNotes,
  MdOutlineAccessTime,
  MdOutlinePerson,
  MdOutlineRadioButtonUnchecked,
  MdOutlineViewKanban,
  MdRefresh,
} from 'react-icons/md'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import TaskMemberAvatar from './TaskMemberAvatar'
import TaskPage2024Column from './TaskPage2024Column/TaskPage2024Column'
import TaskPage2024Task from './TaskPage2024Task/TaskPage2024Task'
import {
  addTaskColumn,
  addTaskCard,
  createTaskCardId,
  getAssignableTaskMembers,
  getTaskDueDateStatus,
  moveTaskCard,
  moveTaskColumn,
  normalizeTaskBoard,
  removeTaskCard,
  removeTaskColumn,
  renameTaskColumn,
  updateTaskCard,
} from './taskBoardUtils'
import './task-page-2024.css'

const formatTaskDate = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Not available'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const makeEditableChecklists = (checklists = []) => checklists.map((checklist) => ({
  ...checklist,
  addingItem: false,
  itemDraft: '',
}))

const toDateTimeLocalValue = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const offset = date.getTimezoneOffset() * 60 * 1000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

const TaskPage2024 = ({ userData = null }) => {
  const [board, setBoard] = React.useState(null)
  const [members, setMembers] = React.useState([])
  const [membersLoading, setMembersLoading] = React.useState(true)
  const [membersError, setMembersError] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const [activeCard, setActiveCard] = React.useState(null)
  const [activeColumn, setActiveColumn] = React.useState(null)
  const [editor, setEditor] = React.useState(null)
  const [deleteCard, setDeleteCard] = React.useState(null)
  const [deleteColumn, setDeleteColumn] = React.useState(null)
  const [addingColumn, setAddingColumn] = React.useState(false)
  const [newColumnName, setNewColumnName] = React.useState('')
  const [ownerFilter, setOwnerFilter] = React.useState('all')

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const loadBoard = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get(`${baseUrl}/admin-task-board`, getRequestHeaders())
      setBoard(normalizeTaskBoard(response.data))
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load the task board.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadBoard()
  }, [loadBoard])

  React.useEffect(() => {
    let stillMounted = true

    const loadMembers = async () => {
      setMembersLoading(true)
      setMembersError('')
      try {
        const response = await axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders())
        if (stillMounted) {
          setMembers(getAssignableTaskMembers(response.data, userData))
        }
      } catch (requestError) {
        if (stillMounted) {
          setMembers(getAssignableTaskMembers([], userData))
          setMembersError('Unable to load the administrator list.')
        }
      } finally {
        if (stillMounted) setMembersLoading(false)
      }
    }

    loadMembers()
    return () => {
      stillMounted = false
    }
  }, [userData])

  const saveColumns = async (columns) => {
    if (!board || saving) return false

    const previousBoard = board
    setBoard({ ...board, columns })
    setSaving(true)
    setError('')

    try {
      const response = await axios.put(
        `${baseUrl}/admin-task-board`,
        { columns, revision: previousBoard.revision },
        getRequestHeaders()
      )
      setBoard(normalizeTaskBoard(response.data))
      return true
    } catch (requestError) {
      const responseData = requestError?.response?.data
      if (requestError?.response?.status === 409 && responseData?.board) {
        setBoard(normalizeTaskBoard(responseData.board))
      } else {
        setBoard(previousBoard)
      }
      setError(responseData?.message || 'Unable to save the task board.')
      return false
    } finally {
      setSaving(false)
    }
  }

  const openCreateTask = (columnId) => {
    setEditor({
      mode: 'create',
      originalColumnId: columnId,
      columnId,
      cardId: '',
      title: '',
      description: '',
      dueDate: '',
      assignedMembers: [],
      checklists: [],
      activity: [],
      createdAt: '',
      updatedAt: '',
      datePickerOpen: false,
      memberPickerOpen: false,
    })
  }

  const openEditTask = (card, columnId) => {
    setEditor({
      mode: 'edit',
      originalColumnId: columnId,
      columnId,
      cardId: card.id,
      title: card.title,
      description: card.description || '',
      dueDate: toDateTimeLocalValue(card.dueDate),
      assignedMembers: card.assignedMembers || [],
      checklists: makeEditableChecklists(card.checklists || []),
      activity: card.activity || [],
      createdAt: card.createdAt,
      updatedAt: card.updatedAt,
      datePickerOpen: false,
      memberPickerOpen: false,
    })
  }

  const toggleEditorMember = (member) => {
    const alreadyAssigned = editor.assignedMembers.some(
      (assignedMember) => assignedMember.userId === member.userId
    )
    setEditor({
      ...editor,
      assignedMembers: alreadyAssigned
        ? editor.assignedMembers.filter(
          (assignedMember) => assignedMember.userId !== member.userId
        )
        : [...editor.assignedMembers, member],
    })
  }

  const updateEditorChecklist = (checklistId, update) => {
    setEditor((currentEditor) => ({
      ...currentEditor,
      checklists: currentEditor.checklists.map((checklist) => (
        checklist.id === checklistId ? update(checklist) : checklist
      )),
    }))
  }

  const addChecklist = () => {
    const checklistNumber = editor.checklists.length + 1
    setEditor({
      ...editor,
      checklists: [
        ...editor.checklists,
        {
          id: createTaskCardId(),
          title: checklistNumber === 1 ? 'Checklist' : `Checklist ${checklistNumber}`,
          hideCompleted: false,
          items: [],
          addingItem: true,
          itemDraft: '',
        },
      ],
    })
  }

  const addChecklistItem = (checklistId) => {
    setEditor((currentEditor) => ({
      ...currentEditor,
      checklists: currentEditor.checklists.map((checklist) => {
        if (checklist.id !== checklistId) return checklist
        const text = checklist.itemDraft.trim()
        if (!text) return checklist
        return {
          ...checklist,
          items: [
            ...checklist.items,
            { id: createTaskCardId(), text, completed: false },
          ],
          itemDraft: '',
          addingItem: true,
        }
      }),
    }))
  }

  const removeChecklist = (checklistId) => {
    setEditor((currentEditor) => ({
      ...currentEditor,
      checklists: currentEditor.checklists.filter(
        (checklist) => checklist.id !== checklistId
      ),
    }))
  }

  const cleanChecklistsForSave = () => editor.checklists.map((checklist) => ({
    id: checklist.id,
    title: checklist.title.trim() || 'Checklist',
    hideCompleted: checklist.hideCompleted === true,
    items: checklist.items
      .map((item) => ({ ...item, text: item.text.trim() }))
      .filter((item) => item.text),
  }))

  const submitEditor = async (event) => {
    event.preventDefault()
    const title = editor.title.trim()
    const description = editor.description.trim()
    const checklists = cleanChecklistsForSave()
    const parsedDueDate = editor.dueDate ? new Date(editor.dueDate) : null
    if (parsedDueDate && Number.isNaN(parsedDueDate.getTime())) {
      setError('Please choose a valid task due date and time.')
      return
    }
    const dueDate = parsedDueDate ? parsedDueDate.toISOString() : null
    if (!title || !board) return

    const now = new Date().toISOString()
    let columns = editor.mode === 'create'
      ? addTaskCard(board.columns, editor.columnId, {
        id: createTaskCardId(),
        title,
        description,
        dueDate,
        assignedMembers: editor.assignedMembers,
        checklists,
        createdAt: now,
        updatedAt: now,
      })
      : updateTaskCard(board.columns, editor.cardId, {
        title,
        description,
        dueDate,
        assignedMembers: editor.assignedMembers,
        checklists,
        updatedAt: now,
      })

    if (editor.mode === 'edit' && editor.columnId !== editor.originalColumnId) {
      columns = moveTaskCard(columns, editor.cardId, editor.columnId)
    }

    if (await saveColumns(columns)) setEditor(null)
  }

  const confirmDelete = async () => {
    if (!deleteCard || !board) return
    const columns = removeTaskCard(board.columns, deleteCard.id)
    if (await saveColumns(columns)) setDeleteCard(null)
  }

  const openDeleteColumn = (columnId) => {
    if (saving || !board || board.columns.length <= 1) return
    const column = board.columns.find((currentColumn) => currentColumn.id === columnId)
    if (column) setDeleteColumn(column)
  }

  const confirmDeleteColumn = async () => {
    if (!deleteColumn || !board || saving) return
    const columns = removeTaskColumn(board.columns, deleteColumn.id)
    if (columns === board.columns) {
      setDeleteColumn(null)
      return
    }
    if (await saveColumns(columns)) setDeleteColumn(null)
  }

  const createColumn = async (event) => {
    event.preventDefault()
    const name = newColumnName.trim()
    if (!name || !board) return

    const columns = addTaskColumn(board.columns, {
      id: createTaskCardId(),
      name,
    })
    if (await saveColumns(columns)) {
      setAddingColumn(false)
      setNewColumnName('')
    }
  }

  const renameColumn = async (columnId, nextName) => {
    const name = nextName.trim()
    const currentColumn = board?.columns.find((column) => column.id === columnId)
    if (!name || !currentColumn || currentColumn.name === name) return true
    return saveColumns(renameTaskColumn(board.columns, columnId, name))
  }

  const handleDragStart = ({ active }) => {
    if (active.data.current?.type === 'card') {
      setActiveColumn(null)
      setActiveCard(active.data.current.card)
    }
    if (active.data.current?.type === 'column') {
      setActiveCard(null)
      setActiveColumn(active.data.current.column)
    }
  }

  const handleDragEnd = async ({ active, over }) => {
    setActiveCard(null)
    setActiveColumn(null)
    if (!over || !board || saving) return

    if (active.data.current?.type === 'column') {
      const targetColumnId = over.data.current?.type === 'card'
        ? over.data.current.card.columnId
        : over.data.current?.columnId || over.id
      const columns = moveTaskColumn(board.columns, active.id, targetColumnId)
      if (columns !== board.columns) await saveColumns(columns)
      return
    }

    const targetColumnId = over.data.current?.type === 'card'
      ? over.data.current.card.columnId
      : over.data.current?.columnId
    const targetCardId = over.data.current?.type === 'card' ? over.id : null
    const columns = moveTaskCard(board.columns, active.id, targetColumnId, targetCardId)

    if (columns !== board.columns) await saveColumns(columns)
  }

  const totalCards = board?.columns?.reduce((total, column) => total + column.cards.length, 0) || 0
  const overdueCards = board?.columns?.reduce((total, column) => (
    total + (column.id === 'done' ? 0 : column.cards.filter((card) => (
      getTaskDueDateStatus(card.dueDate)?.status === 'overdue'
    )).length)
  ), 0) || 0
  const ownerOptions = React.useMemo(() => {
    const ownersById = new Map()
    members.forEach((member) => ownersById.set(member.userId, member))
    board?.columns?.forEach((column) => {
      column.cards.forEach((card) => {
        card.assignedMembers?.forEach((member) => {
          if (member.userId && member.name) ownersById.set(member.userId, member)
        })
      })
    })
    return [...ownersById.values()].sort((ownerA, ownerB) => (
      ownerA.name.localeCompare(ownerB.name, undefined, { sensitivity: 'base' })
    ))
  }, [board, members])
  const displayedColumns = React.useMemo(() => {
    if (!board || ownerFilter === 'all') return board?.columns || []
    return board.columns.map((column) => ({
      ...column,
      cards: column.cards.filter((card) => (
        ownerFilter === 'unassigned'
          ? !card.assignedMembers?.length
          : card.assignedMembers?.some((member) => member.userId === ownerFilter)
      )),
    }))
  }, [board, ownerFilter])
  const visibleCards = displayedColumns.reduce(
    (total, column) => total + column.cards.length,
    0
  )
  const columnPendingDeletion = board?.columns.find((column) => column.id === deleteColumn?.id) || deleteColumn

  return (
    <main className="task-board-page">
      <header className="task-board-page__header">
        <div>
          <div className="task-board-page__eyebrow"><MdOutlineViewKanban /> Shared admin workspace</div>
          <h1>Task Board</h1>
          <p>Move work from idea to completion. Changes are shared with every Atharo administrator.</p>
        </div>
        <div className="task-board-page__header-actions">
          {saving && <div className="task-board-page__saving" role="status">Saving changes…</div>}
          <div className="task-board-page__owner-filter">
            <MdOutlinePerson aria-hidden="true" />
            <select
              aria-label="Filter tasks by owner"
              value={ownerFilter}
              onChange={(event) => setOwnerFilter(event.target.value)}
            >
              <option value="all">All owners</option>
              {ownerOptions.map((owner) => (
                <option key={owner.userId} value={owner.userId}>{owner.name}</option>
              ))}
              <option value="unassigned">Unassigned</option>
            </select>
          </div>
          <div
            className={`task-board-page__overdue ${overdueCards > 0 ? 'has-overdue' : ''}`}
            role="status"
            aria-label={`${overdueCards} overdue ${overdueCards === 1 ? 'task' : 'tasks'}`}
          >
            <MdOutlineAccessTime />
            <strong>{overdueCards}</strong>
            <span>overdue</span>
          </div>
          <span className="task-board-page__total">
            {ownerFilter === 'all' ? totalCards : `${visibleCards} of ${totalCards}`}
            {' '}{totalCards === 1 ? 'task' : 'tasks'}
          </span>
          <button type="button" className="task-board-page__refresh" onClick={loadBoard} disabled={loading || saving}>
            <MdRefresh /> Refresh
          </button>
        </div>
      </header>

      {error && <div className="task-board-page__error" role="alert">{error}</div>}

      {loading && (
        <div className="task-board-page__loading" aria-label="Loading task board">
          <div /><div /><div />
        </div>
      )}

      {!loading && board && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragCancel={() => {
            setActiveCard(null)
            setActiveColumn(null)
          }}
          onDragEnd={handleDragEnd}
        >
          <div className="task-board-page__columns">
            <SortableContext
              items={displayedColumns.map((column) => column.id)}
              strategy={horizontalListSortingStrategy}
            >
              {displayedColumns.map((column) => (
                <TaskPage2024Column
                  key={column.id}
                  column={column}
                  disabled={saving}
                  onAddTask={openCreateTask}
                  onEditTask={openEditTask}
                  onDeleteTask={setDeleteCard}
                  onRenameColumn={renameColumn}
                  onDeleteColumn={openDeleteColumn}
                  canDelete={board.columns.length > 1}
                />
              ))}
            </SortableContext>
            <div className="task-board-add-column">
              {addingColumn ? (
                <form onSubmit={createColumn}>
                  <input
                    autoFocus
                    aria-label="New column name"
                    maxLength="80"
                    value={newColumnName}
                    onChange={(event) => setNewColumnName(event.target.value)}
                    placeholder="Enter column title…"
                  />
                  <div>
                    <button type="submit" disabled={saving || !newColumnName.trim()}>
                      Add column
                    </button>
                    <button
                      type="button"
                      aria-label="Cancel adding column"
                      onClick={() => {
                        setAddingColumn(false)
                        setNewColumnName('')
                      }}
                      disabled={saving}
                    >
                      <MdClose />
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  type="button"
                  className="task-board-add-column__button"
                  onClick={() => setAddingColumn(true)}
                  disabled={saving || board.columns.length >= 20}
                >
                  <MdAdd /> Add another column
                </button>
              )}
            </div>
          </div>
          {createPortal(
            <DragOverlay>
              {activeCard && <TaskPage2024Task card={activeCard} overlay />}
              {activeColumn && (
                <div className="task-board-column-overlay">
                  <header>
                    <strong>{activeColumn.name}</strong>
                    <span>{activeColumn.cards.length}</span>
                  </header>
                  <div className="task-board-column-overlay__cards">
                    {activeColumn.cards.map((card) => (
                      <div className="task-board-column-overlay__card" key={card.id}>
                        <strong>{card.title}</strong>
                        {card.description && <p>{card.description}</p>}
                      </div>
                    ))}
                    {activeColumn.cards.length === 0 && (
                      <div className="task-board-column-overlay__empty">No tasks yet</div>
                    )}
                  </div>
                  <div className="task-board-column-overlay__add"><MdAdd /> Add task</div>
                </div>
              )}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      )}

      {!loading && !board && (
        <div className="task-board-page__empty-state">
          <h2>The board could not be opened</h2>
          <button type="button" onClick={loadBoard}>Try again</button>
        </div>
      )}

      {editor && (
        <div className="task-board-card-modal__backdrop" onMouseDown={() => !saving && setEditor(null)}>
          <form
            className="task-board-card-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-board-editor-title"
            onSubmit={submitEditor}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="task-board-card-modal__header">
              <div className="task-board-card-modal__location">
                <select
                  aria-label="Task column"
                  value={editor.columnId}
                  disabled={saving}
                  onChange={(event) => setEditor({ ...editor, columnId: event.target.value })}
                >
                  {board.columns.map((column) => (
                    <option key={column.id} value={column.id}>{column.name}</option>
                  ))}
                </select>
                <span>/</span>
                <strong>
                  {editor.assignedMembers.length > 0
                    ? editor.assignedMembers.map((member) => member.name.split(' ')[0]).join(', ')
                    : 'Unassigned'}
                </strong>
              </div>
              <button
                type="button"
                className="task-board-card-modal__close"
                aria-label="Close task editor"
                onClick={() => setEditor(null)}
                disabled={saving}
              >
                <MdClose />
              </button>
            </header>

            <div className="task-board-card-modal__body">
              <section className="task-board-card-modal__main">
                <div className="task-board-card-modal__title-row">
                  <MdOutlineRadioButtonUnchecked aria-hidden="true" />
                  <label htmlFor="task-board-title" className="task-board-card-modal__title-label">
                    Task title
                  </label>
                  <textarea
                    id="task-board-title"
                    autoFocus
                    rows="2"
                    maxLength="120"
                    value={editor.title}
                    onChange={(event) => setEditor({ ...editor, title: event.target.value })}
                    placeholder="What needs to be done?"
                  />
                </div>

                <div className="task-board-card-modal__toolbar">
                  <div className="task-board-card-modal__date-action">
                    <button
                      type="button"
                      className={editor.datePickerOpen || editor.dueDate ? 'is-active' : ''}
                      aria-expanded={editor.datePickerOpen}
                      onClick={() => setEditor({
                        ...editor,
                        datePickerOpen: !editor.datePickerOpen,
                        memberPickerOpen: false,
                      })}
                      disabled={saving}
                    >
                      <MdEvent /> Dates
                    </button>
                    {editor.datePickerOpen && (
                      <div className="task-board-date-picker">
                        <strong>Due date</strong>
                        <label htmlFor="task-board-due-date">Date and time</label>
                        <input
                          id="task-board-due-date"
                          type="datetime-local"
                          value={editor.dueDate}
                          onChange={(event) => setEditor({
                            ...editor,
                            dueDate: event.target.value,
                          })}
                        />
                        <div>
                          {editor.dueDate && (
                            <button
                              type="button"
                              className="task-board-date-picker__remove"
                              onClick={() => setEditor({ ...editor, dueDate: '' })}
                            >
                              Remove
                            </button>
                          )}
                          <button
                            type="button"
                            className="task-board-date-picker__done"
                            onClick={() => setEditor({ ...editor, datePickerOpen: false })}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="task-board-card-modal__tool-button"
                    onClick={addChecklist}
                    disabled={saving}
                  >
                    <MdChecklist /> Checklist
                  </button>
                  <div className="task-board-card-modal__member-action">
                    <button
                      type="button"
                      className={editor.memberPickerOpen ? 'is-active' : ''}
                      aria-expanded={editor.memberPickerOpen}
                      onClick={() => setEditor({
                        ...editor,
                        memberPickerOpen: !editor.memberPickerOpen,
                        datePickerOpen: false,
                      })}
                    >
                      <MdGroupAdd /> Members
                    </button>
                    {editor.memberPickerOpen && (
                      <div className="task-board-member-picker">
                        <div className="task-board-member-picker__heading">
                          <strong>Assign members</strong>
                          <span>Select one or more administrators</span>
                        </div>
                        {membersLoading && <p>Loading administrators…</p>}
                        {!membersLoading && members.length === 0 && (
                          <p>{membersError || 'No administrators are available.'}</p>
                        )}
                        {members.map((member) => {
                          const isAssigned = editor.assignedMembers.some(
                            (assignedMember) => assignedMember.userId === member.userId
                          )
                          return (
                            <button
                              key={member.userId}
                              type="button"
                              className="task-board-member-picker__member"
                              aria-pressed={isAssigned}
                              onClick={() => toggleEditorMember(member)}
                            >
                              <TaskMemberAvatar member={member} size="medium" />
                              <span>{member.name}</span>
                              <span className={`task-board-member-picker__check ${isAssigned ? 'is-selected' : ''}`}>
                                {isAssigned && <MdCheck />}
                              </span>
                            </button>
                          )
                        })}
                        {membersError && members.length > 0 && (
                          <p className="task-board-member-picker__error">{membersError}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {editor.assignedMembers.length > 0 && (
                    <div className="task-board-card-modal__assigned-faces">
                      {editor.assignedMembers.map((member) => (
                        <TaskMemberAvatar key={member.userId} member={member} size="medium" />
                      ))}
                    </div>
                  )}
                </div>

                <div className="task-board-card-modal__description">
                  <MdNotes aria-hidden="true" />
                  <div>
                    <label htmlFor="task-board-description">
                      Description <span>Optional</span>
                    </label>
                    <textarea
                      id="task-board-description"
                      rows="7"
                      maxLength="2000"
                      value={editor.description}
                      onChange={(event) => setEditor({ ...editor, description: event.target.value })}
                      placeholder="Add a more detailed description…"
                    />
                  </div>
                </div>

                {editor.checklists.map((checklist) => {
                  const completedItems = checklist.items.filter((item) => item.completed).length
                  const progress = checklist.items.length > 0
                    ? Math.round((completedItems / checklist.items.length) * 100)
                    : 0
                  const visibleItems = checklist.hideCompleted
                    ? checklist.items.filter((item) => !item.completed)
                    : checklist.items

                  return (
                    <section className="task-board-checklist" key={checklist.id}>
                      <div className="task-board-checklist__heading">
                        <MdChecklist aria-hidden="true" />
                        <input
                          aria-label="Checklist title"
                          maxLength="120"
                          value={checklist.title}
                          onChange={({ target: { value } }) => updateEditorChecklist(
                            checklist.id,
                            (currentChecklist) => ({
                              ...currentChecklist,
                              title: value,
                            })
                          )}
                        />
                        <div className="task-board-checklist__heading-actions">
                          {completedItems > 0 && (
                            <button
                              type="button"
                              onClick={() => updateEditorChecklist(
                                checklist.id,
                                (currentChecklist) => ({
                                  ...currentChecklist,
                                  hideCompleted: !currentChecklist.hideCompleted,
                                })
                              )}
                            >
                              {checklist.hideCompleted ? 'Show checked items' : 'Hide checked items'}
                            </button>
                          )}
                          <button type="button" onClick={() => removeChecklist(checklist.id)}>
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="task-board-checklist__progress">
                        <span>{progress}%</span>
                        <div>
                          <span style={{ width: `${progress}%` }} />
                        </div>
                      </div>

                      <div className="task-board-checklist__items">
                        {visibleItems.map((item) => (
                          <div
                            className={`task-board-checklist__item ${item.completed ? 'is-complete' : ''}`}
                            key={item.id}
                          >
                            <input
                              type="checkbox"
                              aria-label={`Mark ${item.text} ${item.completed ? 'incomplete' : 'complete'}`}
                              checked={item.completed}
                              onChange={() => updateEditorChecklist(
                                checklist.id,
                                (currentChecklist) => ({
                                  ...currentChecklist,
                                  items: currentChecklist.items.map((currentItem) => (
                                    currentItem.id === item.id
                                      ? { ...currentItem, completed: !currentItem.completed }
                                      : currentItem
                                  )),
                                })
                              )}
                            />
                            <input
                              className="task-board-checklist__item-text"
                              aria-label="Checklist item"
                              maxLength="500"
                              value={item.text}
                              onChange={({ target: { value } }) => updateEditorChecklist(
                                checklist.id,
                                (currentChecklist) => ({
                                  ...currentChecklist,
                                  items: currentChecklist.items.map((currentItem) => (
                                    currentItem.id === item.id
                                      ? { ...currentItem, text: value }
                                      : currentItem
                                  )),
                                })
                              )}
                            />
                            <button
                              type="button"
                              aria-label={`Delete ${item.text}`}
                              onClick={() => updateEditorChecklist(
                                checklist.id,
                                (currentChecklist) => ({
                                  ...currentChecklist,
                                  items: currentChecklist.items.filter(
                                    (currentItem) => currentItem.id !== item.id
                                  ),
                                })
                              )}
                            >
                              <MdDeleteOutline />
                            </button>
                          </div>
                        ))}
                        {checklist.hideCompleted && visibleItems.length === 0 && checklist.items.length > 0 && (
                          <p className="task-board-checklist__hidden-message">
                            All completed items are hidden.
                          </p>
                        )}
                      </div>

                      {checklist.addingItem ? (
                        <div className="task-board-checklist__add-form">
                          <input
                            autoFocus
                            aria-label={`New item for ${checklist.title}`}
                            maxLength="500"
                            value={checklist.itemDraft}
                            placeholder="Add an item"
                            onChange={({ target: { value } }) => updateEditorChecklist(
                              checklist.id,
                              (currentChecklist) => ({
                                ...currentChecklist,
                                itemDraft: value,
                              })
                            )}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault()
                                addChecklistItem(checklist.id)
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="task-board-checklist__add-primary"
                            disabled={!checklist.itemDraft.trim()}
                            onClick={() => addChecklistItem(checklist.id)}
                          >
                            Add
                          </button>
                          <button
                            type="button"
                            aria-label="Cancel adding checklist item"
                            onClick={() => updateEditorChecklist(
                              checklist.id,
                              (currentChecklist) => ({
                                ...currentChecklist,
                                addingItem: false,
                                itemDraft: '',
                              })
                            )}
                          >
                            <MdClose />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="task-board-checklist__add-button"
                          onClick={() => updateEditorChecklist(
                            checklist.id,
                            (currentChecklist) => ({
                              ...currentChecklist,
                              addingItem: true,
                            })
                          )}
                        >
                          <MdAdd /> Add an item
                        </button>
                      )}
                    </section>
                  )
                })}
              </section>

              <aside className="task-board-card-modal__activity">
                <div className="task-board-card-modal__activity-title">
                  <MdOutlineAccessTime />
                  <h2 id="task-board-editor-title">Card details and activity</h2>
                </div>
                <div className="task-board-card-modal__detail-block">
                  <span>Assigned to</span>
                  {editor.assignedMembers.length > 0 ? editor.assignedMembers.map((member) => (
                    <div className="task-board-card-modal__member-detail" key={member.userId}>
                      <TaskMemberAvatar member={member} size="medium" />
                      <strong>{member.name}</strong>
                    </div>
                  )) : <p>No one is assigned to this card yet.</p>}
                </div>
                {editor.mode === 'edit' && (
                  <div className="task-board-card-modal__timeline">
                    {editor.activity.slice().reverse().map((activity) => (
                      <div className="task-board-card-modal__column-change" key={activity.id}>
                        <span className="task-board-card-modal__timeline-dot" />
                        <p><strong>{activity.actorName}</strong> moved this card</p>
                        <span className="task-board-card-modal__column-transition">
                          {activity.fromColumnName} <span aria-hidden="true">→</span> {activity.toColumnName}
                        </span>
                        <time>{formatTaskDate(activity.occurredAt)}</time>
                      </div>
                    ))}
                    <div>
                      <span className="task-board-card-modal__timeline-dot" />
                      <p>Card created</p>
                      <time>{formatTaskDate(editor.createdAt)}</time>
                    </div>
                    <div>
                      <span className="task-board-card-modal__timeline-dot" />
                      <p>Last updated</p>
                      <time>{formatTaskDate(editor.updatedAt)}</time>
                    </div>
                  </div>
                )}
              </aside>
            </div>

            <footer className="task-board-card-modal__footer">
              <button type="button" className="task-board-modal__cancel" onClick={() => setEditor(null)} disabled={saving}>Cancel</button>
              <button type="submit" className="task-board-modal__primary" disabled={saving || !editor.title.trim()}>
                {saving ? 'Saving…' : editor.mode === 'create' ? 'Create task' : 'Save changes'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {columnPendingDeletion && (
        <div
          className="task-board-modal__backdrop"
          onMouseDown={() => !saving && setDeleteColumn(null)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !saving) setDeleteColumn(null)
          }}
        >
          <div
            className="task-board-modal task-board-modal--delete"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-column-title"
            aria-describedby="delete-column-description"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span>Delete column</span>
            <h2 id="delete-column-title">Delete “{columnPendingDeletion.name}”?</h2>
            <p id="delete-column-description">
              This permanently removes this column
              {columnPendingDeletion.cards.length > 0 && (
                <> and {columnPendingDeletion.cards.length === 1 ? 'the task' : `all ${columnPendingDeletion.cards.length} tasks`} in it</>
              )}
              {' '}from the shared board for every administrator.
              {columnPendingDeletion.cards.length > 0 && ' Move any tasks you want to keep to another column first.'}
            </p>
            {error && <p role="alert">{error}</p>}
            <div className="task-board-modal__actions">
              <button type="button" autoFocus className="task-board-modal__cancel" onClick={() => setDeleteColumn(null)} disabled={saving}>Keep column</button>
              <button type="button" className="task-board-modal__danger" onClick={confirmDeleteColumn} disabled={saving || board?.columns.length <= 1}>{saving ? 'Deleting…' : 'Delete column'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteCard && (
        <div className="task-board-modal__backdrop" onMouseDown={() => !saving && setDeleteCard(null)}>
          <div className="task-board-modal task-board-modal--delete" role="alertdialog" aria-modal="true" aria-labelledby="delete-task-title" onMouseDown={(event) => event.stopPropagation()}>
            <span>Delete task</span>
            <h2 id="delete-task-title">Delete “{deleteCard.title}”?</h2>
            <p>This removes the task from the shared board for every administrator.</p>
            <div className="task-board-modal__actions">
              <button type="button" className="task-board-modal__cancel" onClick={() => setDeleteCard(null)} disabled={saving}>Keep task</button>
              <button type="button" className="task-board-modal__danger" onClick={confirmDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete task'}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

export default TaskPage2024
